/**
 * useCopyToClipboard — the app's single, honest clipboard write.
 *
 * Before this file there were nine hand-rolled copy handlers, seven of
 * which called `navigator.clipboard.writeText` with no fallback and three
 * of which flipped a "Copied" checkmark from a `.then()` that could never
 * tell a rejection from a success. That is the recurring bug in this
 * codebase — the UI asserting a state it never verified — so the rules
 * here are deliberately strict:
 *
 *   - The returned promise resolves to the REAL outcome. `true` only when
 *     a write actually landed. Callers must branch on it.
 *   - `state` goes to 'failed', never 'copied', when the write is refused.
 *   - Two paths are tried: the modern Clipboard API, then legacy
 *     `execCommand('copy')` through a hidden textarea. The legacy path is
 *     NOT optional here: the founder's iPad Air 1 runs Safari 12, which
 *     predates `navigator.clipboard` entirely (see lib/legacy-browser.ts).
 *
 * WebKit gesture rule: the clipboard write must be issued synchronously
 * inside the user-gesture task. `copy()` therefore calls
 * `writeToClipboard()` as its first statement and never awaits anything
 * before it. Callers must hand it text that is ALREADY in memory — do not
 * fetch inside a copy handler.
 *
 * Lifted from the battle-tested implementation that lived privately inside
 * agent/session-pane.tsx (`writeToClipboard`, 2026-06).
 */
import * as React from 'react';

/** Idle → copied | failed, auto-returning to idle after `resetMs`. */
export type CopyState = 'idle' | 'copied' | 'failed';

/**
 * The legacy `execCommand('copy')` path: a throwaway off-screen <textarea>
 * that we focus, select, copy from, and then MUST take back out again.
 *
 * The two invariants below are not theoretical. A probe of the previous
 * version of this function (no try/finally, `removeChild` sitting between
 * the throwing call and the catch) measured, after a single failed copy:
 * textareas in the document 0 → 1, the leaked node still holding the full
 * copied text, and `document.activeElement` left as that TEXTAREA. On a
 * device with no `navigator.clipboard` — i.e. exactly the Safari 12 iPad
 * this fallback exists for — every tap appended one more invisible copy of
 * the message and parked the keyboard inside it, so the next keystrokes
 * went there instead of the composer.
 *
 *   1. The node is removed on EVERY exit path (finally).
 *   2. Focus is put back where it was, so a keyboard user does not lose
 *      their tab position on a copy — including on the success path, which
 *      previously left `activeElement === document.body`.
 */
function copyViaExecCommand(text: string): boolean {
  if (typeof document === 'undefined') return false;
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const ta = document.createElement('textarea');
  try {
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    // `readonly` is the iOS trick that stops the soft keyboard popping up
    // when we focus the throwaway textarea.
    ta.setAttribute('readonly', '');
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    ta.remove();
    // `isConnected` guards the case where the copy source was itself torn
    // down mid-flight; focusing a detached node throws in some engines.
    if (previouslyFocused && previouslyFocused.isConnected) {
      try {
        previouslyFocused.focus();
      } catch {
        /* a non-focusable previous target is not a copy failure */
      }
    }
  }
}

/**
 * Write `text` to the clipboard. Resolves `true` only if a write landed.
 *
 * Never throws: every path is caught and reported as `false`, so a caller
 * can render a truthful failure instead of an optimistic checkmark.
 *
 * NOTE on the empty case: `false` here means "no write landed", which is
 * true both for a refused clipboard and for an empty string. The two are
 * NOT distinguishable from the return value, which is why the empty case
 * is kept away from the UI entirely — <CopyButton> renders nothing when it
 * has no text, so no call site can turn "nothing to copy" into a red
 * "Copy failed" toast. See components/ui/copy-button.tsx.
 */
export async function writeToClipboard(text: string): Promise<boolean> {
  // Nothing to copy is not a success — say so rather than flashing a tick.
  if (!text) return false;
  try {
    // Guarded rather than optional-chained — and NOT because `?.` throws.
    // It does not. Verified in node 2026-07-21:
    //
    //   const nav = {};
    //   const r = nav.clipboard?.writeText('x').then(() => log('THEN RAN'));
    //   // -> r === undefined, "THEN RAN" never printed, nothing thrown
    //   await r;  // -> undefined
    //
    // Optional chaining short-circuits the ENTIRE chain, so a missing
    // `navigator.clipboard` yields `undefined` silently, and `await undefined`
    // RESOLVES. An optional-chained version of this function would therefore
    // `return true` having copied nothing, and would never reach the legacy
    // path below — which is exactly the silent failure this file exists to
    // remove (see brain/source-reader.tsx before 2026-07-21). The explicit
    // guard is what routes a missing clipboard to `copyViaExecCommand`.
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Permission denied / not focused / strict policy — fall through.
  }
  return copyViaExecCommand(text);
}

export interface UseCopyToClipboard {
  /** Current feedback state; drives the icon swap. */
  state: CopyState;
  /** Sugar for `state === 'copied'`. */
  copied: boolean;
  /** Sugar for `state === 'failed'`. */
  failed: boolean;
  /** Copy `text`; resolves to the real outcome. */
  copy: (text: string) => Promise<boolean>;
}

export function useCopyToClipboard(resetMs = 1500): UseCopyToClipboard {
  const [state, setState] = React.useState<CopyState>('idle');
  const timerRef = React.useRef<number | null>(null);
  const mountedRef = React.useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    };
  }, []);

  const copy = React.useCallback(
    async (text: string): Promise<boolean> => {
      // FIRST statement — keeps the WebKit user-gesture chain intact.
      const ok = await writeToClipboard(text);
      if (!mountedRef.current) return ok;
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setState(ok ? 'copied' : 'failed');
      timerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setState('idle');
        timerRef.current = null;
      }, resetMs);
      return ok;
    },
    [resetMs],
  );

  return { state, copied: state === 'copied', failed: state === 'failed', copy };
}
